import { useState } from 'react';
import { useTranslation } from '@parentpath/i18n';
import { User, ArrowLeft, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type UserRole } from './RoleStep';

export type Gender = 'female' | 'male' | 'other';

interface PersonalInfoStepProps {
  birthDate: string;
  gender: Gender | null;
  userRole: UserRole | null;
  onBirthDateChange: (date: string) => void;
  onGenderChange: (gender: Gender) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  submitLabel?: string;
}

export function PersonalInfoStep({
  birthDate,
  gender,
  userRole,
  onBirthDateChange,
  onGenderChange,
  onNext,
  onBack,
  isSubmitting = false,
  error: externalError = null,
  submitLabel,
}: PersonalInfoStepProps) {
  const { t } = useTranslation('onboarding');
  const [localError, setLocalError] = useState<string | null>(null);
  const error = externalError || localError;
  const label = submitLabel ?? t('common.next');

  const genderOptions: { value: Gender; label: string }[] = [
    { value: 'female', label: t('personal.genderFemale') },
    { value: 'male', label: t('personal.genderMale') },
    { value: 'other', label: t('personal.genderOther') },
  ];

  // Determine if gender selection should be shown (only for "partner" role)
  const showGenderSelection = userRole === 'partner';

  // Calculate min/max dates for birth date
  // Min: 100 years ago, Max: 14 years ago (minimum age)
  const today = new Date();
  const maxDate = new Date(today);
  maxDate.setFullYear(today.getFullYear() - 14);
  const minDate = new Date(today);
  minDate.setFullYear(today.getFullYear() - 100);

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDateString = e.target.value;
    onBirthDateChange(newDateString);
    setLocalError(null);
  };

  const handleNext = () => {
    if (!birthDate) {
      setLocalError(t('personal.errorBirthDate'));
      return;
    }

    const date = new Date(birthDate);
    if (date > maxDate) {
      setLocalError(t('personal.errorMinAge'));
      return;
    }

    // Only require gender if it's a partner role
    if (showGenderSelection && !gender) {
      setLocalError(t('personal.errorGender'));
      return;
    }

    onNext();
  };

  // Calculate age for display
  const calculateAge = (): number | null => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge();
  // Only show high-risk age warning for mothers
  const isHighRiskAge = age !== null && age >= 35 && userRole === 'mother';

  // Check if can proceed
  const canProceed = birthDate && (showGenderSelection ? gender : true);

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.back')}
      </button>

      {/* Icon */}
      <div className="w-16 h-16 mx-auto mb-6 bg-pp-orange/20 rounded-full flex items-center justify-center">
        <User className="w-8 h-8 text-pp-orange" />
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2 text-center">{t('personal.title')}</h1>

      {/* Description */}
      <p className="text-white/70 mb-6 text-center">
        {showGenderSelection
          ? t('personal.descriptionGender')
          : t('personal.descriptionAge')}
      </p>

      {/* Birth Date Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-white/80 mb-2">{t('personal.birthDate')}</label>
        <input
          type="date"
          value={birthDate}
          onChange={handleBirthDateChange}
          min={minDate.toISOString().split('T')[0]}
          max={maxDate.toISOString().split('T')[0]}
          className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                     text-white caret-white
                     focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                     outline-none transition-all"
        />
        {age !== null && (
          <p className="mt-2 text-sm text-white/50">
            {t('personal.ageDisplay', { age })}
            {isHighRiskAge && (
              <span className="text-amber-400 ml-1">{t('personal.highRiskAge')}</span>
            )}
          </p>
        )}
      </div>

      {/* Gender Selection - only for Partner role */}
      {showGenderSelection && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-white/80 mb-2">{t('personal.gender')}</label>
          <div className="space-y-3">
            {genderOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => onGenderChange(option.value)}
                className={cn(
                  'w-full flex items-center justify-between p-4 rounded-[10px] border-2 transition-all',
                  gender === option.value
                    ? 'border-pp-orange bg-pp-orange/20'
                    : 'border-white/20 bg-white/5 hover:border-white/30'
                )}
              >
                <span
                  className={cn(
                    'font-medium',
                    gender === option.value ? 'text-white' : 'text-white/90'
                  )}
                >
                  {option.label}
                </span>
                {gender === option.value && (
                  <div className="w-6 h-6 bg-pp-orange rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && <p className="mb-4 text-sm text-red-400 text-center">{error}</p>}

      {/* CTA */}
      <button
        onClick={handleNext}
        disabled={!canProceed || isSubmitting}
        className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {isSubmitting ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          label
        )}
      </button>
    </div>
  );
}
